import VoiceSettingsModal from "@/components/modal/VoiceSettingsModal";
import WhitelistModal from "@/components/modal/WhitelistModal";
import { SubtitleEditor } from "@/components/slides/list/SubtitleEditor";
import {
  GeneratePassageVideoSubtitleDocument,
  GeneratePassageVideoWordBoundariesDocument,
  TextsToSpeechesDocument,
} from "@/generated/graphql";
import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import type { SortableHandleProps } from "@/lib/dnd/components/Sortable";
import { useAudioPlayer } from "@/lib/hooks/shared/useAudioPlayer";
import { useSpellChecker } from "@/lib/hooks/shared/useSpellChecker";
import { useReadingHandlers } from "@/lib/hooks/slides/editor/handlers/reading/useReadingHandlers";
import { useLocalAudioUpload } from "@/lib/hooks/slides/editor/useLocalAudioUpload";
import { useLocalImageUpload } from "@/lib/hooks/slides/editor/useLocalImageUpload";
import { useLocalVideoUpload } from "@/lib/hooks/slides/editor/useLocalVideoUpload";
import { toastAiCredits } from "@/lib/utils/aiCredits";
import { getGraphQLErrorMessage, hasGraphQLErrorCode } from "@/lib/utils/graphqlErrors";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import {
  ErrorCodes,
  MediaUsageContextSchema,
  type Passage,
  type ReadingSlideData,
  type SpellCheckStatus,
  type SubtitleWord,
  type WordBoundary,
} from "@ai-teach/types";
import { Button, IconButton, Modal } from "@ai-teach/ui-web";
import { buildPassageWordBoundaries } from "@ai-teach/utils";
import { useMutation } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { AiFillPicture } from "react-icons/ai";
import { FaMagic, FaMusic, FaPause, FaPlay, FaVideo } from "react-icons/fa";
import { HiChevronDown, HiChevronRight, HiX } from "react-icons/hi";
import { IoSettings } from "react-icons/io5";
import {
  MdCheckCircle,
  MdError,
  MdOutlinePendingActions,
  MdSpellcheck,
  MdSync,
  MdTune,
} from "react-icons/md";
import { RxDragHandleDots2 } from "react-icons/rx";
import { useSlideEditorContext } from "../../../SlideEditorContext";
import { PassageActionButtons } from "./PassageActionButtons";

const PASSAGE_MEDIA_TYPES: { value: "image" | "video"; label: string }[] = [
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
];

interface PassageBlockProps {
  passage: Passage;
  index: number;
  isTextOnly: boolean;
  voiceName: string;
  dragHandle?: SortableHandleProps | undefined;
  defaultExpanded?: boolean;
  misspelledWords: string[];
  onMisspelledWordsChange: (words: string[]) => void;
}

const TIMING_MATCH_WARNING_RATIO = 0.6;

// Approximates the app's boundary-to-token matching closely enough to warn the author; the real
// alignment tolerates more (order, repeats), so this only ever flags a clearly divergent clip.
function countWordsMatchingPassage(boundaries: WordBoundary[], text: string): number {
  const normalize = (value: string) => value.replace(/[^\w-]/g, "").toLowerCase();
  const passageWords = new Set(text.split(/\s+/).map(normalize).filter(Boolean));
  return boundaries.filter((boundary) => passageWords.has(normalize(boundary.text))).length;
}

function toSubtitleWords(boundaries: WordBoundary[]): SubtitleWord[] {
  return boundaries.map((boundary) => ({
    word: boundary.text,
    start: boundary.offset / 1000,
    end: (boundary.offset + boundary.duration) / 1000,
  }));
}

function toWordBoundaries(words: SubtitleWord[]): WordBoundary[] {
  return words.map((word) => ({
    text: word.word,
    offset: word.start * 1000,
    duration: Math.max(word.end - word.start, 0) * 1000,
  }));
}

const getSpellCheckStyles = (status: SpellCheckStatus) => {
  switch (status) {
    case "unchecked":
      return {
        leftBorder: "border-l-4 border-l-amber-400",
        badge: "bg-amber-50 text-amber-700 ring-1 ring-amber-300",
        icon: <MdOutlinePendingActions className="text-xs" />,
        iconStandalone: <MdOutlinePendingActions className="text-amber-500 text-sm shrink-0" />,
        label: "Not Checked",
      };
    case "passed":
      return {
        leftBorder: "border-l-4 border-l-emerald-500",
        badge: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-300",
        icon: <MdCheckCircle className="text-xs" />,
        iconStandalone: <MdCheckCircle className="text-emerald-500 text-sm shrink-0" />,
        label: "Passed",
      };
    case "failed":
      return {
        leftBorder: "border-l-4 border-l-red-500",
        badge: "bg-red-50 text-red-700 ring-1 ring-red-300",
        icon: <MdError className="text-xs" />,
        iconStandalone: <MdError className="text-red-500 text-sm shrink-0" />,
        label: "Has Errors",
      };
  }
};

export function PassageBlock({
  passage,
  index,
  isTextOnly,
  voiceName,
  dragHandle,
  defaultExpanded = false,
  misspelledWords,
  onMisspelledWordsChange,
}: PassageBlockProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isVoiceSettingsOpen, setIsVoiceSettingsOpen] = useState(false);
  const [isWhitelistModalOpen, setIsWhitelistModalOpen] = useState(false);

  // Errors are only visible in the expanded body, so a failing check has to open the block.
  useEffect(() => {
    if (misspelledWords.length > 0) setIsExpanded(true);
  }, [misspelledWords]);

  const dispatch = useAppDispatch();
  const { setActivePanel } = useSlideEditorContext();

  const { image, video, audio, text, id } = passage;
  // Text-only ignores the visual entirely, so its passages read with audio whatever mediaType says.
  const isVideoPassage = !isTextOnly && passage.mediaType === "video";

  const { slide } = useAppSelector((state) => state.slideEditor.present);

  const {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    playbackSpeed,
    hasError,
    togglePlayback,
    changePlaybackSpeed,
    formatTime,
    clearError,
  } = useAudioPlayer(audio?.src);

  if (!slide) return null;

  const readingSlideData = slide.slideData as ReadingSlideData;

  const updateText = (value: string) => {
    onMisspelledWordsChange([]);
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...readingSlideData,
          passages: readingSlideData.passages.map((p) =>
            p.id === id ? { ...p, text: value, spellCheckStatus: "unchecked" as const } : p
          ),
        },
      })
    );
  };

  const handleRemoveMedia = (type: "image" | "video" | "audio") => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...readingSlideData,
          passages: readingSlideData.passages.map((p) =>
            p.id === id ? { ...p, [type]: null } : p
          ),
        },
      })
    );
  };

  // Video passages carry no audio — the clip's soundtrack is the model read — so the switch drops
  // whatever was generated for the image version.
  const setMediaType = (mediaType: "image" | "video") => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...readingSlideData,
          passages: readingSlideData.passages.map((p) =>
            p.id === id ? { ...p, mediaType, ...(mediaType === "video" && { audio: null }) } : p
          ),
        },
      })
    );
  };

  const readingHandlers = useReadingHandlers(slide);
  const { handleFileDrop: handleImageFileDrop, isUploading: isUploadingImage } =
    useLocalImageUpload((media) => readingHandlers.addImage?.(media, id ?? undefined));
  const { handleFileDrop: handleVideoFileDrop, isUploading: isUploadingVideo } =
    useLocalVideoUpload((media) => readingHandlers.addVideo?.(media, id ?? undefined));
  const { handleFileDrop: handleAudioFileDrop } = useLocalAudioUpload((media) =>
    readingHandlers.addAudio?.(media, id ?? undefined)
  );

  const { getMisspelledWord } = useSpellChecker();

  const handleSpellingCheck = async (whitelistOverride?: string[]) => {
    const effectiveWhitelist = whitelistOverride ?? [...(passage.whitelist ?? [])];

    if (!passage.text?.trim()) {
      toast.error("Add passage text first", { id: "spellCheckToast" });
      return;
    }

    toast.loading("Checking spelling...", { id: "spellCheckToast" });

    try {
      const response = await getMisspelledWord({
        variables: {
          text: passage.text,
          whitelist: effectiveWhitelist,
        },
      });

      const words = response.data?.getMisspelledWord || [];

      const newStatus: SpellCheckStatus = words.length === 0 ? "passed" : "failed";

      onMisspelledWordsChange(words.map((word) => word.word));

      dispatch(
        updateSlide({
          slideId: slide.id,
          slideData: {
            ...readingSlideData,
            passages: readingSlideData.passages.map((p) =>
              p.id === id
                ? {
                    ...p,
                    spellCheckStatus: newStatus,
                    ...(whitelistOverride !== undefined && { whitelist: whitelistOverride }),
                  }
                : p
            ),
          },
        })
      );

      if (newStatus === "passed") {
        toast.success("Spell check passed! No errors found.", { id: "spellCheckToast" });
      } else {
        const errorCount = words.length;
        toast.error(`Found ${errorCount} misspelled word${errorCount > 1 ? "s" : ""}`, {
          id: "spellCheckToast",
        });
      }
    } catch (error) {
      console.error("[SpellCheck] error:", error);
      if (toastAiCredits(error, "spellCheckToast")) return;
      toast.error("Failed to check spelling. Please try again.", { id: "spellCheckToast" });
    }
  };

  const handleSaveWhitelist = (whitelist: string[]) => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...readingSlideData,
          passages: readingSlideData.passages.map((p) => (p.id === id ? { ...p, whitelist } : p)),
        },
      })
    );

    handleSpellingCheck(whitelist);
  };

  const [textsToSpeechesMutation] = useMutation(TextsToSpeechesDocument);

  const handleApplyVoiceSettings = (audioData: {
    id: string | null;
    filename: string | null;
    src: string | null;
    voice: string;
    wordBoundaries?: WordBoundary[] | null;
  }) => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...readingSlideData,
          passages: readingSlideData.passages.map((p) =>
            p.id === id
              ? {
                  ...p,
                  audio: {
                    id: audioData.id ?? null,
                    src: audioData.src ?? null,
                    filename: audioData.filename ?? null,
                    voice: audioData.voice,
                    wordBoundaries: audioData.wordBoundaries ?? null,
                  },
                }
              : p
          ),
        },
      })
    );
  };

  const handleGenerateAudio = async () => {
    if (!passage.text?.trim()) {
      toast.error("Add passage text first", { id: "generatingAudioToast" });
      return;
    }

    toast.loading("Generating Audio", { id: "generatingAudioToast" });

    try {
      const result = await textsToSpeechesMutation({
        variables: {
          texts: [passage.text],
          voice: voiceName,
          slideId: slide.id,
          usageContext: MediaUsageContextSchema.Values.slideEditor,
          speed: 0.8,
          pitch: 0,
        },
      });

      const data = result.data?.textsToSpeeches;

      if (!data) {
        throw new Error("Synthesized data not found!");
      }

      const audioData = data[0];

      if (!audioData) {
        throw new Error("Audio file not exist!");
      }

      dispatch(
        updateSlide({
          slideId: slide.id,
          slideData: {
            ...readingSlideData,
            passages: readingSlideData.passages.map((p) =>
              p.id === id
                ? {
                    ...p,
                    audio: {
                      id: audioData.id ?? null,
                      src: audioData.src ?? null,
                      filename: audioData.filename ?? null,
                      voice: voiceName,
                      wordBoundaries: audioData.wordBoundaries ?? null,
                    },
                  }
                : p
            ),
          },
        })
      );

      toast.success("Audio generated", { id: "generatingAudioToast" });
    } catch (error) {
      if (toastAiCredits(error, "generatingAudioToast")) return;
      toast.error("Failed to generate audio!", { id: "generatingAudioToast" });
    }
  };

  const [generatePassageVideoWordBoundariesMutation, { loading: isSyncingVideoWordBoundaries }] =
    useMutation(GeneratePassageVideoWordBoundariesDocument);

  const videoWordBoundaryCount = video?.wordBoundaries?.length ?? 0;
  const canEditVideoTimings = videoWordBoundaryCount > 0 || !!text?.trim();
  const editTimingsTitle = !canEditVideoTimings
    ? "Add passage text first — there are no words to time yet"
    : videoWordBoundaryCount > 0
      ? "Adjust each word's timing against the video by hand"
      : "No timings saved yet — generate the subtitle or fix the timing first";
  const matchingTimingWords = video?.wordBoundaries
    ? countWordsMatchingPassage(video.wordBoundaries, text ?? "")
    : 0;
  const hasTimingMismatch =
    videoWordBoundaryCount > 0 &&
    matchingTimingWords < videoWordBoundaryCount * TIMING_MATCH_WARNING_RATIO;

  const [generatePassageVideoSubtitleMutation, { loading: isGeneratingSubtitle }] = useMutation(
    GeneratePassageVideoSubtitleDocument
  );

  const reportClipError = (err: unknown, noSpeechMessage: string, failureMessage: string) => {
    if (hasGraphQLErrorCode(err, ErrorCodes.SUBTITLE_UNAVAILABLE)) {
      // Nothing was spoken — the author can still work by hand, so not a red failure.
      toast(getGraphQLErrorMessage(err) ?? noSpeechMessage, {
        id: "passageVideoSyncToast",
        icon: "ℹ️",
      });
    } else if (hasGraphQLErrorCode(err, ErrorCodes.AI_CREDITS_EXHAUSTED)) {
      toast.error(
        getGraphQLErrorMessage(err) ??
          "Out of AI credits. Please ask the developer team to top up the OpenAI account.",
        { id: "passageVideoSyncToast", duration: 10000 }
      );
    } else {
      toast.error(failureMessage, { id: "passageVideoSyncToast" });
    }
  };

  const handleGenerateSubtitle = async () => {
    const videoSrc = video?.src;
    if (!videoSrc) return;
    if (
      text?.trim() &&
      !window.confirm("Replace this passage's text with what the video says, and re-time it?")
    ) {
      return;
    }

    try {
      const result = await generatePassageVideoSubtitleMutation({
        variables: { slideId: slide.id, videoSrc },
      });
      const generated = result.data?.generatePassageVideoSubtitle;
      if (!generated) return;
      // Text without timings is worse than no text: it looks finished and never highlights.
      if (generated.wordBoundaries.length === 0) {
        toast.error("Couldn't time the passage against this video. Please try again.", {
          id: "passageVideoSyncToast",
        });
        return;
      }

      readingHandlers.setPassageSubtitle(
        generated.text,
        generated.wordBoundaries.map((w) => ({
          text: w.text,
          offset: w.offset,
          duration: w.duration,
        })),
        id ?? undefined
      );
      toast.success(`Wrote ${generated.wordBoundaries.length} words from the video`, {
        id: "passageVideoSyncToast",
      });
    } catch (err) {
      reportClipError(
        err,
        "This video has no speech to write a passage from.",
        "Couldn't read the passage from this video. Please try again."
      );
    }
  };

  const handleSyncVideoWordBoundaries = async () => {
    const videoSrc = video?.src;
    if (!videoSrc || !text?.trim()) return;

    try {
      const result = await generatePassageVideoWordBoundariesMutation({
        variables: { slideId: slide.id, videoSrc, text },
      });
      const wordBoundaries = (result.data?.generatePassageVideoWordBoundaries ?? []).map((w) => ({
        text: w.text,
        offset: w.offset,
        duration: w.duration,
      }));

      readingHandlers.setVideoWordBoundaries(wordBoundaries, id ?? undefined);

      const matching = countWordsMatchingPassage(wordBoundaries, text);
      if (matching < wordBoundaries.length * TIMING_MATCH_WARNING_RATIO) {
        toast(
          `Synced, but the clip only located ${matching} of ${wordBoundaries.length} words — the rest were estimated. Worth checking by hand.`,
          { id: "passageVideoSyncToast", icon: "⚠️", duration: 8000 }
        );
      } else {
        toast.success(
          `Synced ${wordBoundaries.length} word${wordBoundaries.length === 1 ? "" : "s"}`,
          { id: "passageVideoSyncToast" }
        );
      }
    } catch (err) {
      reportClipError(
        err,
        "This video has no speech to time the text against.",
        "Couldn't time the passage against this video. Please try again."
      );
    }
  };

  const [manualTimings, setManualTimings] = useState<SubtitleWord[] | null>(null);
  const videoDurationSecondsRef = useRef(0);

  const openTimingEditor = () => {
    const existing = video?.wordBoundaries ?? [];
    // Opens empty rather than inventing evenly-spaced timings: a passage has either real timings
    // for every word or none at all, and fabricated rows read as saved work.
    if (existing.length === 0) {
      setManualTimings([]);
      return;
    }

    // Stored timings are a transcript of the text as it was: rebuilding against the passage as it
    // is now keeps each unchanged word's timing and estimates the words the author just altered.
    const reconciled = buildPassageWordBoundaries(
      text ?? "",
      existing.map((boundary) => ({
        word: boundary.text,
        start: boundary.offset / 1000,
        end: (boundary.offset + boundary.duration) / 1000,
      })),
      videoDurationSecondsRef.current
    );
    setManualTimings(toSubtitleWords(reconciled));
  };

  const handleSaveManualTimings = (words: SubtitleWord[]) => {
    readingHandlers.setVideoWordBoundaries(toWordBoundaries(words), id ?? undefined);
    setManualTimings(null);
    toast.success(`Saved ${words.length} word${words.length === 1 ? "" : "s"}`, {
      id: "passageVideoSyncToast",
    });
  };

  const spellCheckStatus = passage.spellCheckStatus || "unchecked";
  const styles = getSpellCheckStyles(spellCheckStatus);

  // Text content section (used in different positions based on isTextOnly)
  const textContent = (
    <div
      className={`flex flex-col gap-1.5 bg-white rounded-md border border-gray-100 overflow-hidden ${
        isTextOnly ? "h-full" : ""
      }`}
    >
      {/* Spell check status bar — colored left border + prominent pill badge */}
      <div
        className={`flex items-center gap-2 px-2 py-1.5 bg-gray-50 border-b border-gray-100 ${styles.leftBorder}`}
      >
        {styles.iconStandalone}
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${styles.badge}`}>
          {styles.label}
        </span>
      </div>

      {misspelledWords.length > 0 && (
        <div className="flex px-2 py-1.5 bg-red-50 border-b border-red-100">
          <div className="flex-1">
            <p className="text-[9px] font-semibold text-red-500 uppercase tracking-wider mb-1">
              Misspelled words
            </p>
            <div className="flex flex-wrap gap-1">
              {misspelledWords.map((word) => (
                <span
                  key={word}
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700 ring-1 ring-red-200"
                >
                  {word}
                </span>
              ))}
            </div>
          </div>
          <Button
            onClick={async () => {
              handleSaveWhitelist([...(passage.whitelist ?? []), ...misspelledWords]);
            }}
            variant="secondary"
          >
            Add to Whitelist
          </Button>
        </div>
      )}

      <textarea
        value={text || ""}
        onChange={(e) => updateText(e.target.value)}
        className={`outline-none resize-none px-2.5 py-1.5 bg-white text-xs leading-relaxed text-gray-800 placeholder:text-gray-300 ${isTextOnly ? "flex-1" : ""}`}
        placeholder="Enter the reading passage here…"
        style={isTextOnly ? undefined : { height: 120 }}
      />

      <div className="flex items-center justify-between px-2 pb-1.5 pt-0.5 gap-1">
        <button
          type="button"
          onClick={() => handleSpellingCheck()}
          disabled={!text?.trim()}
          title={!text?.trim() ? "Add passage text first" : undefined}
          className="flex-1 text-[10px] py-1 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-blue-50"
        >
          Spell Check
        </button>
        <button
          type="button"
          onClick={() => setIsWhitelistModalOpen(true)}
          className="flex-1 text-[10px] py-1 rounded-md bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors font-medium"
        >
          Whitelist
        </button>
      </div>
    </div>
  );

  const imageZone = id ? (
    <DroppableZone
      id={id}
      data={{ id, type: "reading-image" }}
      allowedType={["image"]}
      className="rounded-xl"
      onLocalFileDrop={handleImageFileDrop}
    >
      <div className="relative rounded-md border border-gray-200 bg-white overflow-hidden aspect-[1/1]">
        {isUploadingImage ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1.5">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-[10px] font-medium text-gray-400">Uploading…</p>
          </div>
        ) : image?.src ? (
          <>
            <img
              src={image.src}
              alt={image.filename || "image"}
              className="w-full h-full object-cover"
            />
            <IconButton
              variant="danger"
              size="sm"
              onClick={() => handleRemoveMedia("image")}
              className="absolute right-1.5 top-1.5 !p-0.5"
            >
              <HiX />
            </IconButton>
          </>
        ) : (
          <div
            onClick={() => setActivePanel("Media")}
            className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-center p-2"
          >
            <AiFillPicture className="text-violet-200 text-2xl" />
            <p className="text-[10px] font-medium text-gray-400">
              Drop image from media or local file here
            </p>
          </div>
        )}
      </div>
    </DroppableZone>
  ) : null;

  const videoZone = id ? (
    <DroppableZone
      id={`${id}-video-${index}`}
      data={{ id, type: "reading-video" }}
      allowedType={["video"]}
      className="rounded-xl"
      onLocalFileDrop={handleVideoFileDrop}
    >
      <div className="relative rounded-md border border-gray-200 bg-white overflow-hidden aspect-video">
        {isUploadingVideo ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1.5">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-[10px] font-medium text-gray-400">Uploading…</p>
          </div>
        ) : video?.src ? (
          <>
            {/* biome-ignore lint/a11y/useMediaCaption: authoring preview of the student-facing passage video */}
            <video
              src={video.src}
              controls
              className="w-full h-full object-contain bg-black"
              onLoadedMetadata={(e) => {
                const seconds = e.currentTarget.duration;
                videoDurationSecondsRef.current = Number.isFinite(seconds) ? seconds : 0;
              }}
            />
            <IconButton
              variant="danger"
              size="sm"
              onClick={() => handleRemoveMedia("video")}
              className="absolute right-1.5 top-1.5 !p-0.5 z-10"
            >
              <HiX />
            </IconButton>
          </>
        ) : (
          <div
            onClick={() => setActivePanel("Media")}
            className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-center p-2"
          >
            <FaVideo className="text-violet-200 text-2xl" />
            <p className="text-[10px] font-medium text-gray-400">
              Drop video from media or local file here
            </p>
          </div>
        )}
      </div>
      {video?.src && (
        <div className="flex flex-col gap-1 mt-1">
          <p className="text-[9px] text-gray-400 leading-snug">
            The passage text lights up word-by-word as the video speaks it.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-1.5 px-2 py-1.5 rounded-md border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center justify-center gap-1">
              <button
                type="button"
                onClick={openTimingEditor}
                disabled={!canEditVideoTimings}
                title={editTimingsTitle}
                className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 hover:text-gray-700 border border-gray-200 hover:border-gray-300 px-2 py-1 rounded transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <MdTune className="text-[10px]" />
                Edit subtitle
              </button>
              <button
                type="button"
                onClick={handleGenerateSubtitle}
                disabled={isGeneratingSubtitle || isSyncingVideoWordBoundaries}
                title="Writes the passage text from what the video says, then times it — replaces the current text"
                className="flex items-center gap-1 text-[10px] font-semibold text-violet-600 hover:text-violet-700 border border-dashed border-violet-300 hover:border-violet-400 px-2 py-1 rounded transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isGeneratingSubtitle ? (
                  <>
                    <span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
                    Reading…
                  </>
                ) : (
                  <>
                    <FaMagic className="text-[10px]" />
                    Generate subtitle
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleSyncVideoWordBoundaries}
                disabled={isSyncingVideoWordBoundaries || isGeneratingSubtitle || !text?.trim()}
                title={
                  !text?.trim()
                    ? "Add passage text first, or generate it from the video"
                    : "Re-times your existing words against the video — the words themselves are unchanged"
                }
                className="flex items-center gap-1 text-[10px] font-semibold text-violet-600 hover:text-violet-700 border border-dashed border-violet-300 hover:border-violet-400 px-2 py-1 rounded transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isSyncingVideoWordBoundaries ? (
                  <>
                    <span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
                    Timing…
                  </>
                ) : (
                  <>
                    <MdSync className="text-[10px]" />
                    Fix timing
                  </>
                )}
              </button>
            </div>
          </div>
          {hasTimingMismatch && (
            <p className="flex items-start gap-1 text-[9px] text-amber-700 leading-snug">
              <MdError className="text-[10px] mt-[1px] shrink-0" />
              <span>
                The clip only located {matchingTimingWords} of {videoWordBoundaryCount} words — the
                rest were estimated. Check them by hand, or re-sync after editing the text.
              </span>
            </p>
          )}
        </div>
      )}

      {manualTimings && video?.src && (
        <SubtitleEditor
          src={video.src}
          subtitles={manualTimings}
          isSaving={false}
          lockWords
          onSave={handleSaveManualTimings}
          onClose={() => setManualTimings(null)}
        />
      )}
    </DroppableZone>
  ) : null;

  const fallbackImageStrip = id ? (
    <DroppableZone
      id={`${id}-fallback-image-${index}`}
      data={{ id, type: "reading-image" }}
      allowedType={["image"]}
      className="rounded-md mt-1"
      onLocalFileDrop={handleImageFileDrop}
    >
      <div className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-gray-200 bg-white">
        {image?.src ? (
          <>
            <img
              src={image.src}
              alt={image.filename || "fallback image"}
              className="w-8 h-8 rounded object-cover shrink-0"
            />
            <p className="flex-1 text-[9px] text-gray-400 leading-snug">
              Shown by app versions without video support.
            </p>
            <IconButton
              variant="danger"
              size="sm"
              onClick={() => handleRemoveMedia("image")}
              className="!p-0.5 shrink-0"
            >
              <HiX />
            </IconButton>
          </>
        ) : (
          <div onClick={() => setActivePanel("Media")} className="flex items-center gap-2 w-full">
            <AiFillPicture className="text-violet-200 text-lg shrink-0" />
            <p className="text-[9px] text-gray-400 leading-snug">
              Drop a fallback image — app versions without video support show nothing without one.
            </p>
          </div>
        )}
      </div>
    </DroppableZone>
  ) : null;

  // The image stays authored in video mode: app builds that predate passage video read only
  // `image`, so a video-only passage would reach those students with no picture at all.
  const visualColumn = id ? (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-t-md border-t-2 bg-violet-50/70 dark:bg-violet-950/20 border-b border-violet-100 dark:border-violet-900/30 shrink-0">
        <span className="text-[10px] font-semibold text-violet-500 dark:text-violet-400 uppercase tracking-widest">
          {isVideoPassage ? "Passage video" : "Passage image"}
        </span>
        <div className="flex-1 h-px bg-violet-200/60 dark:bg-violet-800/30" />
        <div className="flex gap-1 shrink-0">
          {PASSAGE_MEDIA_TYPES.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => setMediaType(type.value)}
              className={`px-2 py-0.5 text-[10px] font-semibold rounded border transition-colors ${
                passage.mediaType === type.value
                  ? "bg-violet-500 text-white border-violet-500"
                  : "bg-white text-gray-500 border-gray-200 hover:border-violet-400"
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>
      {isVideoPassage ? (
        <>
          {videoZone}
          {fallbackImageStrip}
        </>
      ) : (
        imageZone
      )}
    </div>
  ) : null;

  // Audio column — shared between both modes
  const audioColumn = id ? (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-t-md border-t-2 bg-blue-50/70 dark:bg-blue-950/20 border-b border-blue-100 dark:border-blue-900/30 shrink-0">
        <span className="text-[10px] font-semibold text-blue-500 dark:text-blue-400 uppercase tracking-widest">
          Passage audio
        </span>
        <div className="flex-1 h-px bg-blue-200/60 dark:bg-blue-800/30" />
        <button
          type="button"
          onClick={handleGenerateAudio}
          disabled={!text?.trim()}
          title={!text?.trim() ? "Add passage text first" : undefined}
          className="flex items-center gap-1.5 text-[10px] font-semibold text-blue-100 bg-blue-900 hover:bg-blue-700 px-2.5 py-1 rounded-full transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-blue-900"
        >
          <FaMagic className="text-[9px]" />
          Generate
        </button>
        {passage.audio && (
          <button
            type="button"
            onClick={() => setIsVoiceSettingsOpen(true)}
            className="flex items-center gap-1.5 text-[10px] font-semibold text-blue-100 bg-blue-400 hover:bg-blue-700 px-2.5 py-1 rounded-full transition-colors whitespace-nowrap"
          >
            <IoSettings className="text-[9px]" />
            Settings
          </button>
        )}
      </div>
      <DroppableZone
        id={`${id}-audio-${index}`}
        data={{ id, type: "reading-audio" }}
        allowedType={["audio"]}
        className="flex-1"
        onLocalFileDrop={handleAudioFileDrop}
      >
        <div className="relative rounded-b-md border border-gray-200 bg-white overflow-hidden h-full">
          {audio?.src ? (
            <div className="w-full h-full flex flex-col justify-center gap-2 px-3 py-2">
              {/* Filename */}
              <p className="text-[10px] font-medium text-gray-600 truncate text-center">
                {audio.filename?.slice(0, 20) || "Passage Audio"}
              </p>

              {/* Progress bar */}
              <div className="space-y-0.5">
                <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all duration-100"
                    style={{
                      width: duration > 0 ? `${(currentTime / duration) * 100}%` : "0%",
                    }}
                  />
                </div>
                <div className="flex justify-between text-[9px] text-gray-400">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration || 0)}</span>
                </div>
              </div>

              {/* Controls — play button + speed buttons in a single row */}
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={togglePlayback}
                  className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all ${
                    isPlaying ? "bg-blue-600 hover:bg-blue-700" : "bg-blue-500 hover:bg-blue-600"
                  }`}
                >
                  {isPlaying ? (
                    <FaPause className="text-white text-[9px]" />
                  ) : (
                    <FaPlay className="text-white text-[9px] ml-0.5" />
                  )}
                </button>
                <div className="flex gap-0.5 flex-1">
                  {[1.0, 1.5, 2.0].map((speed) => (
                    <button
                      type="button"
                      key={speed}
                      onClick={() => {
                        changePlaybackSpeed(speed);
                        if (audioRef.current) {
                          audioRef.current.playbackRate = speed;
                        }
                      }}
                      className={`flex-1 text-[10px] py-1 rounded-md transition-colors ${
                        playbackSpeed === speed
                          ? "bg-blue-100 text-blue-700 font-semibold"
                          : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      }`}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              </div>

              {/* Remove audio */}
              <IconButton
                variant="danger"
                size="sm"
                onClick={() => handleRemoveMedia("audio")}
                className="absolute right-1.5 top-1.5 !p-0.5"
              >
                <HiX className="text-xs" />
              </IconButton>
            </div>
          ) : (
            <div
              onClick={() => setActivePanel("Media")}
              className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-center p-2"
            >
              <FaMusic className="text-gray-200 text-xl" />
              <p className="text-[10px] font-medium text-gray-400">Drop audio from media here</p>
            </div>
          )}
        </div>
      </DroppableZone>
    </div>
  ) : null;

  return (
    <div className="flex flex-col rounded-lg border border-gray-200 bg-gray-50">
      {/* Always mounted (not gated by isExpanded) so the collapsed-header Play button still works. */}
      {audio?.src && (
        <audio ref={audioRef} src={audio.src} preload="metadata">
          <track kind="captions" srcLang="en" default />
        </audio>
      )}

      {/* HEADER — drag handle + label + collapse + status + delete */}
      <div
        className={`flex items-center gap-1.5 px-2 py-1.5 bg-white ${
          isExpanded ? "border-b border-gray-100 rounded-t-lg" : "rounded-lg"
        }`}
      >
        {dragHandle && (
          <button
            type="button"
            ref={dragHandle.ref}
            {...dragHandle.attributes}
            {...dragHandle.listeners}
            aria-label="Drag to reorder"
            className="shrink-0 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing"
          >
            <RxDragHandleDots2 className="text-sm" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className="flex items-center gap-1.5 min-w-0 flex-1 text-left"
        >
          <span className="text-gray-400 shrink-0">
            {isExpanded ? (
              <HiChevronDown className="text-sm" />
            ) : (
              <HiChevronRight className="text-sm" />
            )}
          </span>
          <h3 className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide shrink-0">
            Passage {index + 1}
          </h3>
          {!isExpanded && (
            <span className="text-[11px] text-gray-400 truncate">
              {text?.trim() || "No text yet"}
            </span>
          )}
        </button>
        {!isExpanded && (
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              title={text?.trim() ? "Spell check" : "Add passage text first"}
              disabled={!text?.trim()}
              onClick={() => handleSpellingCheck()}
              className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              <MdSpellcheck className="text-sm" />
            </button>
            {!isVideoPassage && (
              <>
                <button
                  type="button"
                  title={text?.trim() ? "Generate audio" : "Add passage text first"}
                  disabled={!text?.trim()}
                  onClick={handleGenerateAudio}
                  className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <FaMagic className="text-xs" />
                </button>
                <button
                  type="button"
                  title={!audio?.src ? "Generate audio first" : isPlaying ? "Pause" : "Play audio"}
                  disabled={!audio?.src}
                  onClick={togglePlayback}
                  className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  {isPlaying ? <FaPause className="text-xs" /> : <FaPlay className="text-xs" />}
                </button>
              </>
            )}
          </div>
        )}
        <span
          className={`shrink-0 text-[9px] font-semibold px-2 py-0.5 rounded-full ${styles.badge}`}
        >
          {styles.label}
        </span>
        <PassageActionButtons index={index} />
      </div>

      {/* BODY */}
      {isExpanded && (
        <div className="flex flex-col gap-2 p-2">
          {isTextOnly ? (
            /* Text-only: text on left, audio on right */
            <div className="grid grid-cols-2 gap-2 aspect-[2/1]">
              {textContent}
              {audioColumn}
            </div>
          ) : (
            <>
              {/* Text block below image + audio grid */}
              {textContent}

              {/* Visual (image or video) + Audio side by side; video takes the full width alone */}
              <div className={`grid gap-2 ${isVideoPassage ? "grid-cols-1" : "grid-cols-2"}`}>
                {visualColumn}
                {!isVideoPassage && audioColumn}
              </div>
            </>
          )}
        </div>
      )}

      <Modal isOpen={hasError} onClose={clearError} title="Error" style="danger" size="sm">
        <p className="text-center text-sm text-muted-foreground">
          The audio file is corrupted, please change the audio file.
        </p>
        <div className="flex justify-center mt-4">
          <Button variant="primary" size="sm" onClick={clearError}>
            OK
          </Button>
        </div>
      </Modal>

      {isVoiceSettingsOpen && passage.audio && (
        <VoiceSettingsModal
          audioData={passage.audio}
          onClose={() => setIsVoiceSettingsOpen(false)}
          text={passage.text}
          slideId={slide.id}
          voice={voiceName}
          onSave={handleApplyVoiceSettings}
        />
      )}

      <WhitelistModal
        isOpen={isWhitelistModalOpen}
        onClose={() => setIsWhitelistModalOpen(false)}
        initialWhitelist={passage.whitelist || []}
        onSave={handleSaveWhitelist}
        onRemoveWord={(whitelist) =>
          dispatch(
            updateSlide({
              slideId: slide.id,
              slideData: {
                ...readingSlideData,
                passages: readingSlideData.passages.map((p) =>
                  p.id === id ? { ...p, whitelist } : p
                ),
              },
            })
          )
        }
      />
    </div>
  );
}
